import { createRoute, useNavigate, Link } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import type { CurrencyCode } from '@kora/types';
import { meLayoutRoute } from './meLayout.js';
import { requestLoan } from '../api/advances.js';
import { formatMoney } from '../lib/format.js';
import { ApiError } from '../api/client.js';
import { Alert } from '../components/Alert.js';

/**
 * /me/loans/request — employee-initiated loan application (ADR-0009 D11,
 * PR ⑦d). Submits a `requested` advance for HR review. The three-layer gate
 * (statutory cap / concurrency / single-currency) runs server-side and any
 * failure surfaces here; no terms are binding until HR approves.
 */
export const myLoansRequestRoute = createRoute({
  getParentRoute: () => meLayoutRoute,
  path: '/me/loans/request',
  component: RequestLoanPage,
});

function RequestLoanPage(): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [kind, setKind] = useState<'loan' | 'salary_advance'>('loan');
  const [principalAmount, setPrincipal] = useState('');
  const [currency, setCurrency] = useState('');
  const [requestedTermMonths, setTerm] = useState('6');
  const [requestPurpose, setPurpose] = useState('');
  const [consented, setConsented] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: () =>
      requestLoan({
        kind,
        principalAmount,
        currency,
        requestedTermMonths: Number(requestedTermMonths),
        requestPurpose,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['me-advances'] });
      await navigate({ to: '/me/loans' });
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Could not submit your application.'),
  });

  const monthly =
    principalAmount && Number(requestedTermMonths) > 0
      ? (Number(principalAmount) / Number(requestedTermMonths)).toFixed(2)
      : '—';

  return (
    <section className="p-8 space-y-6 max-w-xl">
      <nav className="text-xs text-gray-500">
        <Link to="/me/loans" className="hover:underline">My loans</Link> / Request
      </nav>
      <header>
        <h1 className="font-serif text-3xl">Request a loan</h1>
        <p className="text-sm text-gray-400">Submit an application for HR review. You'll be notified once it's approved or declined.</p>
      </header>

      <form
        className="rounded border border-gray-900 p-5 space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          submit.mutate();
        }}
      >
        <Field label="Type">
          <select
            aria-label="Type"
            value={kind}
            onChange={(e) => setKind(e.target.value as 'loan' | 'salary_advance')}
            className="rounded border border-gray-800 bg-gray-950 px-2 py-1 text-sm text-gray-200"
          >
            <option value="loan">Loan</option>
            <option value="salary_advance">Salary advance</option>
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Amount">
            <input aria-label="Amount" value={principalAmount} onChange={(e) => setPrincipal(e.target.value)} className="rounded border border-gray-800 bg-gray-950 px-2 py-1 text-sm text-gray-200" />
          </Field>
          <Field label="Currency">
            <input aria-label="Currency" value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} placeholder="e.g. CDF" className="rounded border border-gray-800 bg-gray-950 px-2 py-1 text-sm text-gray-200" />
          </Field>
          <Field label="Repayment term (months)">
            <input aria-label="Repayment term (months)" type="number" min="1" value={requestedTermMonths} onChange={(e) => setTerm(e.target.value)} className="rounded border border-gray-800 bg-gray-950 px-2 py-1 text-sm text-gray-200" />
          </Field>
          <Field label="Est. monthly deduction">
            <output className="block rounded border border-gray-900 bg-gray-950/50 px-2 py-1 text-sm text-gray-400">{monthly ? formatMoney(monthly, currency as CurrencyCode) : '—'}</output>
          </Field>
        </div>
        <Field label="Purpose">
          <textarea aria-label="Purpose" value={requestPurpose} onChange={(e) => setPurpose(e.target.value)} rows={2} className="rounded border border-gray-800 bg-gray-950 px-2 py-1 text-sm text-gray-200" />
        </Field>

        <label className="flex items-start gap-2 text-xs text-gray-400">
          <input type="checkbox" checked={consented} onChange={(e) => setConsented(e.target.checked)} className="mt-0.5" />
          I authorise repayment via deductions from my salary once this loan is approved and disbursed.
        </label>

        {err ? <Alert variant="error">{err}</Alert> : null}

        <button
          type="submit"
          disabled={submit.isPending || !consented || !principalAmount || !currency || !requestPurpose}
          className="rounded bg-white text-black px-4 py-2 text-sm font-medium hover:bg-gray-200 disabled:opacity-50"
        >
          {submit.isPending ? 'Submitting…' : 'Submit application'}
        </button>
      </form>
    </section>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return (
    <label className="flex flex-col gap-1 text-xs text-gray-400">
      {label}
      {children}
    </label>
  );
}
